import React, { useEffect, useRef, useState } from 'react';
import { defaultAxiosInstance } from '../../api/defaultInstance';
import * as XLSX from 'xlsx';
import '../../css/ReportUrlModal.css';

function ReportUrlModal({ adCampaignMetaId, placeName, onClose, onSaved }) {
  const [urls, setUrls] = useState(['']);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef(null);

  // adCampaignMetaId를 숫자로 변환
  const numericMetaId = typeof adCampaignMetaId === 'string' 
    ? parseInt(adCampaignMetaId, 10) 
    : adCampaignMetaId;

  useEffect(() => {
    const fetchUrls = async () => {
      try {
        setIsLoading(true);
        const response = await defaultAxiosInstance.get('/advertisement/admin/report-urls', {
          params: { adCampaignMetaId: numericMetaId },
        });
        const data = response.data?.data;
        const fetchedUrls = Array.isArray(data?.urls) ? data.urls : [];
        setUrls(fetchedUrls.length > 0 ? fetchedUrls : ['']);
      } catch (error) {
        console.error('리포트 URL 조회 실패:', error);
        setUrls(['']);
      } finally {
        setIsLoading(false);
      }
    };

    if (numericMetaId) {
      fetchUrls();
    }
  }, [numericMetaId]);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.keyCode === 27) {
        if (!isSaving) {
          onClose();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, isSaving]);

  const handleUrlChange = (index, value) => {
    setUrls(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleAddRow = () => {
    setUrls(prev => [...prev, '']);
  };

  const handleExcelButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleExcelChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const data = new Uint8Array(event.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        // 1열(0번째 컬럼), 2번째 행(index 1)부터 끝까지
        const extracted = rows
          .slice(1)
          .map(row => (row && row[0] ? String(row[0]).trim() : ''))
          .filter(v => v);

        if (extracted.length === 0) {
          alert('엑셀에서 읽을 수 있는 URL이 없습니다.');
          return;
        }

        setUrls(extracted);
      } catch (error) {
        console.error('엑셀 파싱 실패:', error);
        alert('엑셀 파일을 읽는 중 오류가 발생했습니다.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleSubmit = async () => {
    const cleaned = urls.map(u => u.trim()).filter(Boolean);
    if (cleaned.length === 0) {
      alert('최소 1개 이상의 URL을 입력해주세요.');
      return;
    }

    try {
      setIsSaving(true);
      await defaultAxiosInstance.post('/advertisement/admin/report-urls', {
        adCampaignMetaId: numericMetaId,
        urls: cleaned,
      });
      alert('리포트 저장을 완료했습니다.');
      if (onSaved) {
        onSaved();
      }
      onClose();
    } catch (error) {
      console.error('리포트 URL 저장 실패:', error);
      alert('리포트 URL 저장 중 오류가 발생했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="report-url-modal-overlay" onClick={onClose}>
      <div className="report-url-modal" onClick={(e) => e.stopPropagation()}>
        <div className="report-url-modal-header">
          <span className="report-url-title">{placeName || '플레이스명'}</span>
          <div className="report-url-header-buttons">
            <button
              type="button"
              className="report-url-add-row-btn"
              onClick={handleAddRow}
              disabled={isLoading || isSaving}
            >
              + 행추가
            </button>
            <button
              type="button"
              className="report-url-excel-btn"
              onClick={handleExcelButtonClick}
              disabled={isLoading || isSaving}
            >
              엑셀대량등록
            </button>
            <input
              type="file"
              accept=".xlsx,.xls"
              ref={fileInputRef}
              style={{ display: 'none' }}
              onChange={handleExcelChange}
            />
          </div>
        </div>

        <div className="report-url-list-wrapper">
          {isLoading ? (
            <div className="report-url-loading">불러오는 중...</div>
          ) : (
            <div className="report-url-list">
              {urls.map((url, index) => (
                <div key={index} className="report-url-row">
                  <span className="report-url-index">{index + 1}.</span>
                  <input
                    type="text"
                    className="report-url-input"
                    placeholder="URL 입력"
                    value={url}
                    onChange={(e) => handleUrlChange(index, e.target.value)}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="report-url-footer">
          <button
            type="button"
            className="report-url-cancel-btn"
            onClick={onClose}
            disabled={isSaving}
          >
            취소
          </button>
          <button
            type="button"
            className="report-url-submit-btn"
            onClick={handleSubmit}
            disabled={isSaving || isLoading}
          >
            {isSaving ? '등록 중...' : '등록'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ReportUrlModal;


